import React, { useState, useEffect } from "react";
import { getAuditLogs, getAuditLogsForVideo, AuditLog } from "../../services/youtubeAuditService";
import { History, ArrowRight, Layers, Activity, Clock, ChevronRight } from "lucide-react";
import VersionComparison from "./VersionComparison";

const AuditHistory: React.FC = () => {
  const [allLogs, setAllLogs] = useState<AuditLog[]>([]);
  const [historyMode, setHistoryMode] = useState<"activity" | "version">("activity");
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [compareSelection, setCompareSelection] = useState<AuditLog[]>([]);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    setAllLogs(getAuditLogs());
  }, []);

  const uniqueVideos = Array.from(new Set(allLogs.map(log => log.videoId))).map(id => {
    return allLogs.find(log => log.videoId === id)!;
  });

  const videoLogs = selectedVideoId ? getAuditLogsForVideo(selectedVideoId) : [];

  const handleVersionClick = (log: AuditLog) => {
    if (compareSelection.find(s => s.id === log.id)) {
      setCompareSelection(compareSelection.filter(s => s.id !== log.id));
    } else if (compareSelection.length < 2) {
      setCompareSelection([...compareSelection, log].sort((a,b) => a.version - b.version));
    }
  };

  return (
    <div style={{ animation: "fadeIn 0.4s ease" }}>
      {/* Internal Tab Navigation */}
      <div style={{ display: "flex", gap: "24px", marginBottom: "32px", borderBottom: "1px solid #f1f5f9" }}>
         <button 
            onClick={() => setHistoryMode("activity")}
            style={{ 
                padding: "0 0 12px 0", border: "none", background: "none", fontSize: "14px", fontWeight: 700, 
                color: historyMode === "activity" ? "#6366f1" : "#94a3b8",
                borderBottom: historyMode === "activity" ? "2px solid #6366f1" : "2px solid transparent",
                cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "all 0.2s"
            }}
         >
            <Activity size={16}/> Global Activity Feed
         </button>
         <button 
            onClick={() => setHistoryMode("version")}
            style={{ 
                padding: "0 0 12px 0", border: "none", background: "none", fontSize: "14px", fontWeight: 700, 
                color: historyMode === "version" ? "#6366f1" : "#94a3b8",
                borderBottom: historyMode === "version" ? "2px solid #6366f1" : "2px solid transparent",
                cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "all 0.2s"
            }}
         >
            <Layers size={16}/> Version Explorer (v1 vs vX)
         </button>
      </div>

      {historyMode === "activity" ? (
        /* MODE 1: GLOBAL TIMELINE */
        <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: "20px" }}>
                <div style={{ position: "absolute", left: "20px", top: 0, bottom: 0, width: "2px", background: "#f1f5f9" }}></div>
                {allLogs.map((log) => (
                    <div key={log.id} style={{ display: "flex", gap: "24px", alignItems: "flex-start", position: "relative", paddingLeft: "44px" }}>
                        <div style={{ position: "absolute", left: "14px", top: "12px", width: "14px", height: "14px", borderRadius: "50%", background: "#6366f1", border: "3px solid #fff" }}></div>
                        <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #f1f5f9", flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                                    <span style={{ fontSize: "11px", fontWeight: 800, color: "#6366f1", textTransform: "uppercase" }}>{log.videoTitle}</span>
                                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>&bull; {new Date(log.timestamp).toLocaleString()}</span>
                                </div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>{log.changeDescription}</p>
                                <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>Version {log.version} saved into ledger</p>
                            </div>
                            <button 
                                onClick={() => { setSelectedVideoId(log.videoId); setHistoryMode("version"); }}
                                style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", color: "#6366f1", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
                            >
                                Explore Versions <ChevronRight size={14}/>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      ) : (
        /* MODE 2: VERSION EXPLORER */
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "32px", animation: "fadeIn 0.3s ease" }}>
            <div style={{ background: "#fff", padding: "24px", borderRadius: "20px", border: "1px solid #f1f5f9" }}>
                <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <History size={18} color="#6366f1" /> Content Audited
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {uniqueVideos.map(v => (
                        <div 
                            key={v.videoId} 
                            onClick={() => setSelectedVideoId(v.videoId)}
                            style={{ 
                                padding: "12px", borderRadius: "10px", cursor: "pointer",
                                background: selectedVideoId === v.videoId ? "#eff6ff" : "transparent",
                                border: "1px solid",
                                borderColor: selectedVideoId === v.videoId ? "#6366f1" : "transparent"
                            }}
                        >
                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: selectedVideoId === v.videoId ? "#1e40af" : "#1e293b" }}>{v.videoTitle}</p>
                            <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#64748b" }}>{allLogs.filter(l => l.videoId === v.videoId).length} versions saved</p>
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ background: "#fff", padding: "32px", borderRadius: "24px", border: "1px solid #f1f5f9" }}>
                {!selectedVideoId ? (
                    <div style={{ height: "400px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                        <Layers size={48} style={{ marginBottom: "16px", opacity: 0.5 }} />
                        <p>Select a video to view its deep version history</p>
                    </div>
                ) : (
                    <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
                            <h3 style={{ margin: 0 }}>Version History Timeline</h3>
                            <button 
                                disabled={compareSelection.length !== 2}
                                onClick={() => setShowComparison(true)}
                                style={{ 
                                    padding: "10px 20px", borderRadius: "12px", border: "none",
                                    background: compareSelection.length === 2 ? "#6366f1" : "#f1f5f9",
                                    color: compareSelection.length === 2 ? "#fff" : "#94a3b8",
                                    fontWeight: 700, cursor: compareSelection.length === 2 ? "pointer" : "default",
                                    display: "flex", alignItems: "center", gap: "8px", transition: "all 0.2s"
                                }}
                            >
                                <ArrowRight size={18}/> Compare v{compareSelection[0]?.version} vs v{compareSelection[1]?.version}
                            </button>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "16px", position: "relative" }}>
                            <div style={{ position: "absolute", left: "20px", top: 0, bottom: 0, width: "2px", background: "#f1f5f9" }}></div>
                            {videoLogs.map((log) => (
                                <div 
                                    key={log.id} 
                                    onClick={() => handleVersionClick(log)}
                                    style={{ 
                                        display: "flex", gap: "24px", alignItems: "center", position: "relative", paddingLeft: "44px", cursor: "pointer"
                                    }}
                                >
                                    <div style={{ 
                                        position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)",
                                        width: "14px", height: "14px", borderRadius: "50%",
                                        background: compareSelection.find(s => s.id === log.id) ? "#6366f1" : "#fff",
                                        border: "3px solid #6366f1", zIndex: 2
                                    }}></div>
                                    <div style={{ 
                                        flex: 1, padding: "16px", borderRadius: "16px", border: "1px solid #f1f5f9",
                                        background: compareSelection.find(s => s.id === log.id) ? "#f8f9ff" : "#fff",
                                        boxShadow: compareSelection.find(s => s.id === log.id) ? "0 4px 12px rgba(99, 102, 241, 0.1)" : "none"
                                    }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                            <span style={{ fontSize: "12px", fontWeight: 800, color: "#6366f1" }}>VERSION {log.version}</span>
                                            <span style={{ fontSize: "11px", color: "#94a3b8" }}>{new Date(log.timestamp).toLocaleString()}</span>
                                        </div>
                                        <p style={{ margin: 0, fontWeight: 600, color: "#1e293b", fontSize: "15px" }}>{log.changeDescription}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
      )}

      {showComparison && compareSelection.length === 2 && (
        <VersionComparison 
            vA={compareSelection[0]} 
            vB={compareSelection[1]} 
            onClose={() => setShowComparison(false)} 
        />
      )}
    </div>
  );
};

export default AuditHistory;
